// ============================================================
//  LoginModal.jsx

import { useState, useEffect, useContext } from "react";
import { Mail, Lock, Eye, EyeOff, X } from "lucide-react";
import "../../../assets/css/customer/auth/Login.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import { useNavigate } from "react-router-dom";

export default function LoginModal({ onClose, onSwitchToSignup }) {
  const { baseUrl, setLoggedInCustomer, RegExpChecker, setAuthToken } =
    useContext(AppContext);

  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // ── Submit ─────────────────────────────────────────────────
  const handleSignIn = () => {
    const emailErr = RegExpChecker("email", email);
    const passwordErr = RegExpChecker("password", password);
    const errs = { ...emailErr, ...passwordErr };

    // Filter out empty strings — only keep actual errors
    const hasErrors = Object.values(errs).some((msg) => msg !== "");
    if (hasErrors) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    axios
      .post(`${baseUrl}/customer/auth/customerLogin`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success) {
          setLoggedInCustomer(response.data.customer);

          localStorage.setItem(
            "loggedInCustomer",
            JSON.stringify(response.data.customer),
          );
          localStorage.setItem("authToken", response.data.token);
          setAuthToken(response.data.token);
          setIsSubmitting(false);

          onClose();
        } else {
          setErrors({
            general: response.data.message || "Login failed. Please try again.",
          });
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        console.log("error:", error);
      });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="lm-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Login"
    >
      <div className="lm-modal">
        {/* Close */}
        <button
          className="lm-close-btn"
          onClick={onClose}
          aria-label="Close modal"
        >
          <X size={14} strokeWidth={2.5} />
        </button>

        {/* Header */}
        <div className="lm-header">
          <h2 className="lm-title">Welcome back</h2>
          <p className="lm-subtitle">Sign in to continue your journey</p>
        </div>

        {/* Body */}
        <div className="lm-form-body">
          {/* General error */}
          {errors.general && (
            <div className="lm-general-error">⚠ {errors.general}</div>
          )}

          {/* Email */}
          <div className="lm-field">
            <label className="lm-label" htmlFor="lm-email">
              Email
            </label>
            <div className="lm-input-wrap">
              <span className="lm-input-icon">
                <Mail size={16} />
              </span>
              <input
                id="lm-email"
                type="email"
                className={`lm-input${errors.email ? " lm-input-error" : ""}`}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors(RegExpChecker("email", e.target.value));
                }}
                autoComplete="email"
              />
            </div>
            {errors.email && (
              <span className="lm-error-msg">⚠ {errors.email}</span>
            )}
          </div>

          {/* Password */}
          <div className="lm-field">
            <label className="lm-label" htmlFor="lm-pw">
              Password
            </label>
            <div className="lm-input-wrap">
              <span className="lm-input-icon">
                <Lock size={16} />
              </span>
              <input
                id="lm-pw"
                type={showPw ? "text" : "password"}
                className={`lm-input${errors.password ? " lm-input-error" : ""}`}
                placeholder="Your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors(RegExpChecker("password", e.target.value));
                }}
                autoComplete="current-password"
              />
              <button
                className="lm-eye-btn"
                type="button"
                onClick={() => setShowPw((p) => !p)}
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            </div>
            {errors.password && (
              <span className="lm-error-msg">⚠ {errors.password}</span>
            )}
          </div>

          {/* Forgot */}
          <div className="lm-row lm-row-end">
            <button
              className="lm-forgot-link"
              type="button"
              onClick={() => {
                navigate("/forget-password");
              }}
            >
              Forgot password?
            </button>
          </div>

          {/* Submit */}
          <button
            className="lm-submit-btn"
            type="button"
            onClick={handleSignIn}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Signing in…" : "Sign In"}
          </button>

          {/* Switch */}
          <p className="lm-switch-text">
            Don't have an account?{" "}
            <button
              className="lm-switch-link"
              type="button"
              onClick={onSwitchToSignup}
            >
              Sign up free
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
